import { schema } from "normalizr";
import { Record, Map } from "immutable";

import { TranslatableMap } from "@localisation/models";
import { NormalisedEntities } from "@orm/models";

interface MetaMap {
    size?: number;
}

export interface IImage {
    id?: number;
    label?: string;
    alt?: string;
    formatExt?: string;
    fullsizeFileId?: number;
    thumbMediumFileId?: number;
    thumbSmallFileId?: number;
    createdAt?: Date;
    updatedAt?: Date;
    deletedAt?: Date;
    fullsizeFile?: IFile;
    thumbMediumFile?: IFile;
    thumbSmallFile?: IFile;
}

export interface IFile {
    id?: number;
    file?: string;
    origFilename?: string;
    disk?: string;
    url?: string;
    formatExt?: string;
    meta?: MetaMap;
    createdAt?: Date;
    updatedAt?: Date;
    deletedAt?: Date;
    format?: IFileFormat;
}

export interface IFileFormat {
    ext?: string;
    label?: TranslatableMap;
    labelShort?: TranslatableMap;
    mimeType?: string;
}

const ImageRecord = Record({
    id:                null,
    label:             null,
    alt:               null,
    formatExt:         null,
    fullsizeFileId:    null,
    thumbMediumFileId: null,
    thumbSmallFileId:  null,
    createdAt:         null,
    updatedAt:         null,
    deletedAt:         null,
    fullsizeFile:      null,
    thumbMediumFile:   null,
    thumbSmallFile:    null,
});

const FileRecord = Record({
    id:           null,
    file:         null,
    origFilename: null,
    disk:         null,
    url:          null,
    formatExt:    null,
    meta:         null,
    createdAt:    null,
    updatedAt:    null,
    deletedAt:    null,
    format:       null,
});

const FileFormatRecord = Record({
    ext: null,
    label: null,
    labelShort: null,
    mimeType: null,
});

export class Image extends ImageRecord implements IImage {
    public id: number;
    public label: string;
    public alt: string;
    public formatExt: string;
    public fullsizeFileId: number;
    public thumbMediumFileId: number;
    public thumbSmallFileId: number;
    public createdAt: Date;
    public updatedAt: Date;
    public deletedAt: Date;
    public fullsizeFile: File;
    public thumbMediumFile: File;
    public thumbSmallFile: File;
}

export class File extends FileRecord implements IFile {
    public id: number;
    public file: string;
    public origFilename: string;
    public disk: string;
    public url: string;
    public formatExt: string;
    public meta: MetaMap;
    public createdAt: Date;
    public updatedAt: Date;
    public deletedAt: Date;
    // Relations
    public format: FileFormat;

    constructor(params?: IFile) {
        // Create the relation records.
        if (params != null) {
            if ("format" in params && params.format != null &&
            typeof params.format === "object" &&
            !(params.format instanceof FileFormat)
        ) {
                params.format = new FileFormat(params.format);
            }
        }

        super(params);
    }
}

export class FileFormat extends FileFormatRecord implements IFileFormat {
    public ext: string;
    public label: TranslatableMap;
    public labelShort: TranslatableMap;
    public mimeType: string;
}

export interface FilesInternalState {
    entities: FilesNormalisedEntities;
}

export interface FilesState {
    files: FilesInternalState;
}

export interface FilesNormalisedEntities extends NormalisedEntities {
    images?: Map<number, Image>;
    files?: Map<number, File>;
    fileFormats?: Map<string, FileFormat>;
}

// Define the normalised version of charts.
export const NFileFormat = new schema.Entity("fileFormats", {}, {
    idAttribute: "ext",
});

export const NFile = new schema.Entity("files", {
    format: NFileFormat,
});

export const NImage = new schema.Entity("images", {
    fullsize_file: NFile,
    thumb_medium_file: NFile,
    thumb_small_file: NFile,
});
